import React, { useEffect, useState, useRef } from 'react';
import clsx from 'clsx';
import { MarkdownContent } from './markdownContent';
import { AudioVisualizer } from 'react-audio-visualize';
import { getSpeechUrl } from '../utils/getSpeechUrl';
import { useAudio } from '../context/audio';
import { sendEvent } from '../actions/analytics';

const SpeakerIcon = ({ size = 20, className = '' }) => (
  <svg
    height={size}
    width={size}
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
    className={className}
  >
    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
  </svg>
);

const StopIcon = ({ size = 20, className = '' }) => (
  <svg
    height={size}
    width={size}
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
    className={className}
  >
    <rect x="6" y="6" width="12" height="12" />
  </svg>
);

const ChatMessage = ({ message }) => (
  <Message
    content={message.content}
    isAudio={message.link && message.link !== null}
    speechUrl={message.link}
    isSelf={message.type === 'query'}
  />
);

// NOTE: this wrapper is intentionally a <div>, not a <p>. Assistant messages
// can contain block-level markdown (headings, lists, …) which are invalid
// inside a <p> — the browser would auto-close the <p> before them and break
// the layout.
const Message = ({ content, speechUrl, isAudio, isSelf }) => (
  <div
    className={clsx('my-2 p-2 liv-message font-bold rounded-lg', {
      'bg-primary opacity-70 font-normal self-end mb-8': isSelf,
    })}
  >
    {!isSelf ? (
      <AssistantMessage content={content} speechUrl={getSpeechUrl(speechUrl)} />
    ) : (
      <UserMessage isAudio={isAudio} speechUrl={speechUrl} content={content} />
    )}
  </div>
);

const AssistantMessage = ({ content, speechUrl }) => {
  const { currentUrl, toggleMessage } = useAudio();
  const isPlaying = currentUrl === speechUrl;

  return (
    <div className="relative group assistant-message">
      <MarkdownContent>{content}</MarkdownContent>
      {speechUrl && (
        <div className="m-0 flex opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-300 z-10">
          <button
            onClick={() => toggleMessage(speechUrl)}
            title={isPlaying ? 'Stop' : 'Listen'}
            className="p-1 rounded-full text-white bg-transparent hover:bg-white/20 disabled:opacity-50"
          >
            {isPlaying ? <StopIcon size={16} /> : <SpeakerIcon size={16} />}
          </button>
        </div>
      )}
    </div>
  );
};

const UserMessage = ({ isAudio, speechUrl, content }) => (
  <>{isAudio ? <AudioMessage speechUrl={speechUrl} /> : content}</>
);

const AudioMessage = ({ speechUrl }) => {
  const [audioBlob, setAudioBlob] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);
  const visualizerRef = useRef(null);
  let intervalRef = useRef(null);

  useEffect(() => {
    const fetchAudioBlob = async () => {
      try {
        const response = await fetch(speechUrl);
        const blob = await response.blob();
        setAudioBlob(blob);
      } catch (error) {
        console.error('Error fetching audio blob:', error);
      }
    };

    if (speechUrl) {
      fetchAudioBlob();
    }
  }, [speechUrl]);

  useEffect(() => {
    if (audioBlob) {
      const audioUrl = URL.createObjectURL(audioBlob);
      audioRef.current = new Audio(audioUrl);
      audioRef.current.onended = () => {
        setIsPlaying(false);
        clearInterval(intervalRef.current);
        setCurrentTime(0);
      };
    }
  }, [audioBlob]);

  const toggleAudio = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        clearInterval(intervalRef.current);
        sendEvent('play_self_off', {});
      } else {
        audioRef.current.play();
        intervalRef.current = setInterval(() => {
          setCurrentTime(audioRef.current.currentTime);
        }, 100); // Update every 100ms
        sendEvent('play_self_on', {});
      }
      setIsPlaying(!isPlaying);
    }
  };

  useEffect(() => {
    return () => {
      clearInterval(intervalRef.current); // Cleanup interval on component unmount
    };
  }, []);

  return (
    <div className="flex items-center gap-2">
      <button onClick={toggleAudio} className="play-button">
        {isPlaying ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="30"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <rect x="6" y="5" width="4" height="14" />
            <rect x="14" y="5" width="4" height="14" />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="30"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <polygon points="5,3 19,12 5,21" />
          </svg>
        )}
      </button>
      {audioBlob && (
        <AudioVisualizer
          ref={visualizerRef}
          blob={audioBlob}
          height={40}
          width={200}
          currentTime={currentTime}
        />
      )}
    </div>
  );
};

export { ChatMessage };
